import type { ApiResponse, PaginationMeta } from "@/types/api";

/** Simulate network latency so loading states render realistically in demo mode. */
export function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function reqId(): string {
  return globalThis.crypto?.randomUUID?.() ?? "mock-req-id";
}

/** Wrap raw data in the standard ApiResponse envelope. */
export function ok<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    status_code: 200,
    message: "OK",
    data,
    error: null,
    meta: { request_id: reqId(), timestamp: new Date().toISOString() },
  };
}

/** Paginate an array into an ApiResponse<T[]> with pagination meta. */
export function paginate<T>(rows: T[], page = 1, pageSize = 20): ApiResponse<T[]> {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pagination: PaginationMeta = {
    page,
    page_size: pageSize,
    total_items: total,
    total_pages: totalPages,
    has_next: page < totalPages,
    has_previous: page > 1,
  };
  return {
    success: true,
    status_code: 200,
    message: "OK",
    data: rows.slice(start, start + pageSize),
    error: null,
    meta: { request_id: reqId(), timestamp: new Date().toISOString(), pagination },
  };
}

/** Case-insensitive substring match across several fields. */
export function matchSearch(fields: (string | undefined | null)[], search?: string): boolean {
  if (!search) return true;
  const q = search.toLowerCase();
  return fields.some((f) => f?.toLowerCase().includes(q));
}
